/**
 * Person Card business-logic hook
 *
 * Hosts all state, derived values, and mutation orchestration for the modal so
 * the UI components stay presentational. Mirrors the original
 * EditMemberDialog.handleSave orchestration so behavior parity is preserved.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBillingEntities } from '@features/billing-entities';
import { useServiceFacilities } from '@features/service-facilities';
import {
  type BundleWithStatus,
  getAddOnsIncludedInTier,
  getMonthlyAmountCents,
  useBundleCatalog,
  useGetFullSubscriptionDetailsQuery,
} from '@features/subscriptions';
import { useUserId } from '@features/user';
import { useEventTracking } from '@services/statsig';
import type {
  ProviderLicenseType,
  UpdateMemberBillingInfoRequest,
} from '@store/generated/organizations';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useAddMemberBundleMutation,
  useChangeMemberBundleMutation,
  useGetMemberDetailQuery,
  useGetMembershipInfoQuery,
  useRemoveMemberBundleMutation,
  useRemoveMemberMutation,
  useUpdateMemberBillingInfoMutation,
  useUpdateMemberProfileMutation,
  useUpdateMemberRolesMutation,
  useUpdateMembershipInfoMutation,
} from '../../store/api';
import type { OrganizationMemberTableRow } from '../../types';

export interface BillingFormData {
  billing_entity_id?: string;
  service_facility_id?: string;
  taxonomy_code?: string;
  // Narrowed to the form's union so MemberBillingInfoForm accepts the shape
  // without a runtime cast (string | '' is treated as "cleared").
  license_type?: ProviderLicenseType | null;
  license_number?: string;
  license_state?: string;
  npi_number?: string;
  first_name?: string;
  last_name?: string;
  communications_name?: string;
  signing_name?: string;
  facility_address?: string;
  facility_address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  fax_number?: string;
  tax_id?: string;
  tax_id_type?: string;
  place_of_service?: string;
}

export interface ProfileFormData {
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone_num: string;
}

// Defaults used as the baseline shape for change detection. license_type stays
// null when cleared since the form's union excludes the empty string.
const EMPTY_BILLING: { [K in keyof BillingFormData]: NonNullable<BillingFormData[K]> | '' } = {
  billing_entity_id: '',
  service_facility_id: '',
  taxonomy_code: '',
  license_type: '',
  license_number: '',
  license_state: '',
  npi_number: '',
  first_name: '',
  last_name: '',
  communications_name: '',
  signing_name: '',
  facility_address: '',
  facility_address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax_number: '',
  tax_id: '',
  tax_id_type: '',
  place_of_service: '',
};

interface UsePersonCardArgs {
  member: OrganizationMemberTableRow | null;
  open: boolean;
  onSuccess: () => void;
}

export function usePersonCard({ member, open, onSuccess }: UsePersonCardArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const currentUserId = useUserId();

  // Catalog + subscription state
  const {
    tiers,
    addOns,
    getCompatibleAddOnsForDisplay,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();
  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery();
  const isAnnualBilling = subscriptionDetails?.billing_interval === 'year';
  const isTrialing = !!subscriptionDetails?.is_trialing;

  // Billing entity / facility selects in MemberBillingInfoForm
  const { entities: billingEntities, isLoading: entitiesLoading } = useBillingEntities();
  const { facilities: serviceFacilities, isLoading: facilitiesLoading } = useServiceFacilities();

  // Per-member queries
  const { data: memberDetail, isLoading: isLoadingDetail } = useGetMemberDetailQuery(
    { userId: member?.userId || '' },
    { skip: !open || !member?.userId },
  );
  const { data: membershipInfo, isLoading: isLoadingMembershipInfo } = useGetMembershipInfoQuery(
    { userId: member?.userId || '' },
    { skip: !open || !member?.userId },
  );

  // Mutations
  const [updateRoles, { isLoading: isUpdatingRoles }] = useUpdateMemberRolesMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateMemberProfileMutation();
  const [updateBillingInfo, { isLoading: isUpdatingBillingInfo }] =
    useUpdateMemberBillingInfoMutation();
  const [updateMembershipInfo, { isLoading: isUpdatingMembershipInfo }] =
    useUpdateMembershipInfoMutation();
  const [changeMemberBundle, { isLoading: isChangingBundle }] = useChangeMemberBundleMutation();
  const [addMemberBundle, { isLoading: isAddingBundle }] = useAddMemberBundleMutation();
  const [removeMemberBundle, { isLoading: isRemovingBundle }] = useRemoveMemberBundleMutation();
  const [removeMember, { isLoading: isDeactivating }] = useRemoveMemberMutation();

  // Roles + bundles selection (committed via Save / Confirm)
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedTierId, setSelectedTierId] = useState<string>('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());
  const [needsCosign, setNeedsCosign] = useState(false);
  // '' = unassigned (visible to every supervisor's queue)
  const [supervisorUserId, setSupervisorUserId] = useState<string>('');
  const [canPrepareOnBehalf, setCanPrepareOnBehalf] = useState(false);

  // Profile + billing — local form state, autosaved per project pattern
  const [profileData, setProfileData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone_num: '',
  });
  const [billingData, setBillingData] = useState<BillingFormData>({});

  // Hydrate state when detail / membership land
  useEffect(() => {
    if (!memberDetail || !open) return;
    setSelectedRoles(new Set(memberDetail.roles || []));

    const currentTier = memberDetail.bundles?.find(
      b => b.bundle_type === 'tier' && b.status === 'active',
    );
    setSelectedTierId(currentTier?.bundle_id || '');

    const currentAddOnIds =
      memberDetail.bundles
        ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
        .map(b => b.bundle_id) || [];
    setSelectedAddOnIds(new Set(currentAddOnIds));

    setProfileData({
      first_name: memberDetail.first_name || '',
      last_name: memberDetail.last_name || '',
      email: memberDetail.email || '',
      mobile_phone_num: memberDetail.mobile_phone_num || '',
    });

    const billing = memberDetail.billing_info;
    setBillingData({
      billing_entity_id: billing?.billing_entity_id || '',
      service_facility_id: billing?.service_facility_id || '',
      taxonomy_code: billing?.taxonomy_code || '',
      license_type: billing?.license_type ?? null,
      license_number: billing?.license_number || '',
      license_state: billing?.license_state || '',
      npi_number: billing?.npi_number || '',
      first_name: billing?.first_name || '',
      last_name: billing?.last_name || '',
      communications_name: billing?.communications_name || '',
      signing_name: billing?.signing_name || '',
      facility_address: billing?.facility_address || '',
      facility_address2: billing?.facility_address2 || '',
      city: billing?.city || '',
      state: billing?.state || '',
      zip: billing?.zip || '',
      phone: billing?.phone || '',
      fax_number: billing?.fax_number || '',
      tax_id: billing?.tax_id || '',
      tax_id_type: billing?.tax_id_type || '',
      place_of_service: billing?.place_of_service || '',
    });
  }, [memberDetail, open]);

  useEffect(() => {
    if (!membershipInfo || !open) return;
    setNeedsCosign(membershipInfo.needs_cosign);
    setSupervisorUserId(membershipInfo.supervisor_user_id ?? '');
    setCanPrepareOnBehalf(membershipInfo.can_prepare_prescriptions_on_behalf ?? false);
  }, [membershipInfo, open]);

  // GDN-1119: prepare-on-behalf is ops-support-only — clear when role removed.
  const isOperationsSupport = selectedRoles.has(ROLES.OPERATIONS_SUPPORT);
  useEffect(() => {
    if (!isOperationsSupport && canPrepareOnBehalf) {
      setCanPrepareOnBehalf(false);
    }
  }, [isOperationsSupport, canPrepareOnBehalf]);

  // The "Current" chip anchors to the member's currently-paid tier — stays put even
  // when the user picks a different card. Mirrors the prototype + GDN-1634 contract.
  const currentTierAssignment = memberDetail?.bundles?.find(
    b => b.bundle_type === 'tier' && b.status === 'active',
  );
  const currentTierBundleId = currentTierAssignment?.bundle_id;

  const isClinician = selectedRoles.has(ROLES.CLINICIAN);

  // Add-ons the selected roles may hold without a paid seat, driven by the
  // catalog's eligible_roles (e.g. JotRx for supervisors — GDN-2606).
  const roleEligibleAddOns = useMemo(
    () =>
      isClinician
        ? []
        : addOns.filter(a => a.eligible_roles?.some(r => selectedRoles.has(r))),
    [isClinician, addOns, selectedRoles],
  );
  const canSelectBundles = isClinician || roleEligibleAddOns.length > 0;

  // Keep bundle selection consistent when roles change (mirrors
  // InviteUserDialog): a non-clinician selection holds no tier, and any
  // selected add-ons the current roles are no longer eligible for are pruned
  // — otherwise commitChanges would silently keep billing the stale tier and
  // add-ons after e.g. a clinician → supervisor demotion.
  useEffect(() => {
    if (isClinician) return;
    if (selectedTierId) setSelectedTierId('');
    const eligibleIds = new Set(roleEligibleAddOns.map(a => a.bundle_id));
    setSelectedAddOnIds(prev => {
      const pruned = Array.from(prev).filter(id => eligibleIds.has(id));
      return pruned.length === prev.size ? prev : new Set(pruned);
    });
  }, [isClinician, roleEligibleAddOns, selectedTierId]);

  // Add-ons visible in the modal: union of
  //   (a) add-ons in the tier's `compatible_addon_ids` (priced toggles), and
  //   (b) add-ons whose grants are fully covered by the tier's grants
  //       (locked "Included" rows — driven by the catalog's grants[] field
  //       via getAddOnsIncludedInTier, NOT hardcoded per tier).
  // The current backend catalog encoding makes (a) and (b) disjoint — tiers
  // with full coverage publish `compatible_addon_ids: []` (e.g. Everything in
  // services/ehr_api/config/bundles.yaml), so the union pulls grants-covered
  // add-ons in as locked rows. If the catalog ever evolves to also let a
  // covered add-on be separately purchased ((a) ∩ (b) ≠ ∅), the union
  // continues to render the right rows; the lock comes from `isIncluded`.
  const availableAddOns = useMemo(() => {
    // Non-clinicians: only the add-ons their roles are eligible for, with no
    // tier compatibility involved (they hold no tier).
    if (!isClinician) return roleEligibleAddOns;
    const compatible = getCompatibleAddOnsForDisplay(selectedTierId || null);
    if (!selectedTierId) return compatible;
    const tier = tiers.find(t => t.bundle_id === selectedTierId);
    if (!tier) return compatible;
    const compatIds = new Set(compatible.map(a => a.bundle_id));
    const grantsIncluded = getAddOnsIncludedInTier(tier, addOns).filter(
      a => !compatIds.has(a.bundle_id),
    );
    return [...compatible, ...grantsIncluded];
  }, [
    isClinician,
    roleEligibleAddOns,
    getCompatibleAddOnsForDisplay,
    selectedTierId,
    tiers,
    addOns,
  ]);

  // ── Cost math ───────────────────────────────────────────────────────────────
  // Same formula as MemberTable cost column: tier monthly + add-ons not covered
  // by tier grants. Returned in cents so callers can `formatPrice` consistently.
  const costForSelection = useCallback(
    (tierId: string, addOnIds: Set<string>): { cents: number; currency: string } => {
      // Tier is optional: a member without a paid seat (e.g. a supervisor
      // holding JotRx) has add-on costs only.
      const tier = tiers.find(t => t.bundle_id === tierId);

      const memberAddOnBundles: BundleWithStatus[] = Array.from(addOnIds)
        .map(id => addOns.find(a => a.bundle_id === id))
        .filter((b): b is BundleWithStatus => b != null);

      const includedIds = tier
        ? new Set(
            getAddOnsIncludedInTier(tier, memberAddOnBundles).map(a => a.bundle_id),
          )
        : new Set<string>();
      const paidAddOns = memberAddOnBundles.filter(a => !includedIds.has(a.bundle_id));

      const tierCents = tier ? getMonthlyAmountCents(tier, isAnnualBilling) : 0;
      const addOnsCents = paidAddOns.reduce(
        (sum, a) => sum + getMonthlyAmountCents(a, isAnnualBilling),
        0,
      );
      return {
        cents: tierCents + addOnsCents,
        currency: tier?.currency ?? memberAddOnBundles[0]?.currency ?? 'usd',
      };
    },
    [tiers, addOns, isAnnualBilling],
  );

  const currentCost = useMemo(() => {
    const tierId = currentTierBundleId || '';
    const addOnIds = new Set(
      memberDetail?.bundles
        ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
        .map(b => b.bundle_id) || [],
    );
    return costForSelection(tierId, addOnIds);
  }, [costForSelection, currentTierBundleId, memberDetail]);

  const projectedCost = useMemo(
    () => costForSelection(selectedTierId, selectedAddOnIds),
    [costForSelection, selectedTierId, selectedAddOnIds],
  );

  // ── Change detection ────────────────────────────────────────────────────────
  const hasRolesChanges = useMemo(() => {
    if (!memberDetail) return false;
    const current = new Set(memberDetail.roles || []);
    if (current.size !== selectedRoles.size) return true;
    for (const r of selectedRoles) if (!current.has(r)) return true;
    return false;
  }, [memberDetail, selectedRoles]);

  const hasSupervisorChange = useMemo(() => {
    if (!membershipInfo) return false;
    return supervisorUserId !== (membershipInfo.supervisor_user_id ?? '');
  }, [membershipInfo, supervisorUserId]);

  const hasMembershipChanges = useMemo(() => {
    if (!membershipInfo) return false;
    return (
      needsCosign !== membershipInfo.needs_cosign ||
      hasSupervisorChange ||
      canPrepareOnBehalf !== (membershipInfo.can_prepare_prescriptions_on_behalf ?? false)
    );
  }, [membershipInfo, needsCosign, hasSupervisorChange, canPrepareOnBehalf]);

  const hasPlanChanges = useMemo(() => {
    if (!memberDetail) return false;
    const currentTierId = currentTierBundleId || '';
    if (selectedTierId !== currentTierId) return true;
    const currentAddOns = new Set(
      memberDetail.bundles
        ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
        .map(b => b.bundle_id) || [],
    );
    if (currentAddOns.size !== selectedAddOnIds.size) return true;
    for (const id of selectedAddOnIds) if (!currentAddOns.has(id)) return true;
    return false;
  }, [memberDetail, currentTierBundleId, selectedTierId, selectedAddOnIds]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleRole = useCallback((role: string) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }, []);

  const changeTier = useCallback(
    (newTierId: string) => {
      setSelectedTierId(newTierId);
      // Drop add-ons that are no longer compatible with the new tier.
      const compatible = new Set(
        getCompatibleAddOnsForDisplay(newTierId).map(a => a.bundle_id),
      );
      setSelectedAddOnIds(prev => {
        const filtered = new Set([...prev].filter(id => compatible.has(id)));
        return filtered.size !== prev.size ? filtered : prev;
      });
    },
    [getCompatibleAddOnsForDisplay],
  );

  const toggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      return next;
    });
  }, []);

  // Persist profile changes. Used by autosave (debounce + on-blur).
  const persistProfile = useCallback(
    async (data: ProfileFormData) => {
      if (!member) return;
      await updateProfile({
        userId: member.userId,
        updateMemberProfileRequest: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          mobile_phone_num: data.mobile_phone_num,
        },
      }).unwrap();
    },
    [member, updateProfile],
  );

  // Persist billing changes — diffs the payload against the original detail so
  // only modified fields hit the wire (empty string → null = explicit clear).
  const persistBilling = useCallback(
    async (data: BillingFormData) => {
      if (!member || !memberDetail) return;
      const original = (memberDetail.billing_info ?? {}) as Record<string, string | null | undefined>;
      const payload: UpdateMemberBillingInfoRequest = {};
      const apply = (field: keyof BillingFormData) => {
        const next = data[field] ?? '';
        const prev = original[field] ?? '';
        if (next === prev) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        (payload as any)[field] = next === '' ? null : next;
      };
      (Object.keys(EMPTY_BILLING) as Array<keyof BillingFormData>).forEach(apply);
      if (Object.keys(payload).length === 0) return;
      await updateBillingInfo({
        userId: member.userId,
        updateMemberBillingInfoRequest: payload,
      }).unwrap();
    },
    [member, memberDetail, updateBillingInfo],
  );

  // Roles + plan + membership commit (footer Save / Confirm). Profile + billing
  // autosave independently, so they're intentionally excluded here.
  const commitChanges = useCallback(async () => {
    if (!member || !memberDetail) return;

    try {
      if (hasRolesChanges) {
        const previous = new Set(memberDetail.roles || []);
        await updateRoles({
          userId: member.userId,
          providerUpdateRolesRequest: { roles: Array.from(selectedRoles) },
        }).unwrap();
        clinicians.memberRoleChanged({
          member_id: member.userId,
          role_count: selectedRoles.size,
          roles_added: Array.from(selectedRoles).filter(r => !previous.has(r)).length,
          roles_removed: Array.from(previous).filter(r => !selectedRoles.has(r)).length,
        });
      }

      if (hasMembershipChanges) {
        await updateMembershipInfo({
          userId: member.userId,
          updateMembershipInfoRequest: {
            needs_cosign: needsCosign,
            can_prepare_prescriptions_on_behalf: canPrepareOnBehalf,
            // Omitted = preserved server-side; explicit null clears the
            // assignment, so only send the field when it actually changed.
            ...(hasSupervisorChange
              ? { supervisor_user_id: supervisorUserId || null }
              : {}),
          },
        }).unwrap();
      }

      if (canSelectBundles && hasPlanChanges) {
        const currentTierId = currentTierBundleId || '';
        const currentAddOnSet = new Set(
          memberDetail.bundles
            ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
            .map(b => b.bundle_id) || [],
        );

        // Tier change first (atomic swap; falls through to add when no current).
        let tierChanged = false;
        if (selectedTierId !== currentTierId) {
          tierChanged = true;
          if (currentTierId && selectedTierId && currentTierAssignment) {
            await changeMemberBundle({
              userId: member.userId,
              assignmentId: currentTierAssignment.assignment_id,
              adminAddBundleRequest: { bundle_id: selectedTierId },
            }).unwrap();
          } else if (selectedTierId) {
            await addMemberBundle({
              userId: member.userId,
              adminAddBundleRequest: { bundle_id: selectedTierId },
            }).unwrap();
          } else if (currentTierId && currentTierAssignment) {
            // Tier cleared (e.g. clinician → supervisor demotion): remove it
            // explicitly. The roles update above may have already canceled it
            // server-side (role-change cleanup cancels role-ineligible
            // bundles), in which case the BE rejects a second cancel — treat
            // that as already-done rather than failing the whole save.
            try {
              await removeMemberBundle({
                userId: member.userId,
                assignmentId: currentTierAssignment.assignment_id,
              }).unwrap();
            } catch (err) {
              // Tolerate only the double-cancel outcomes: 404 (assignment
              // gone) or the BE's 400 cancel_error ("Can only cancel active
              // assignments"). Anything else rethrows to the outer catch so
              // the save reports failure instead of a false success.
              const e = err as {
                status?: number | string;
                data?: { error_code?: string };
              };
              const alreadyCanceled =
                e?.status === 404 ||
                (e?.status === 400 && e?.data?.error_code === 'cancel_error');
              if (!alreadyCanceled) throw err;
            }
          }
        }

        // Belt-and-suspenders: drop add-ons whose grants the selected tier
        // already covers. handleTierChange filters against compatible_addon_ids,
        // but the BE has no server-side guard against double-billing a
        // tier-covered add-on (confirmed against bundle_subscription_service.py),
        // so any leak through the FE path would charge the customer for
        // capabilities they already have. This grants-based check matches
        // `getAddOnsIncludedInTier` so the wire payload can never contain a
        // redundant assignment regardless of catalog encoding.
        const selectedTierBundle = tiers.find(t => t.bundle_id === selectedTierId);
        const tierCoveredAddOnIds = selectedTierBundle
          ? new Set(
              getAddOnsIncludedInTier(selectedTierBundle, addOns).map(a => a.bundle_id),
            )
          : new Set<string>();

        const addOnsToAdd = Array.from(selectedAddOnIds).filter(
          id => !currentAddOnSet.has(id) && !tierCoveredAddOnIds.has(id),
        );
        const addOnsToRemove = Array.from(currentAddOnSet).filter(id => !selectedAddOnIds.has(id));

        for (const addOnId of addOnsToAdd) {
          await addMemberBundle({
            userId: member.userId,
            adminAddBundleRequest: { bundle_id: addOnId },
          }).unwrap();
        }
        for (const addOnId of addOnsToRemove) {
          const assignment = memberDetail.bundles?.find(
            b => b.bundle_id === addOnId && b.bundle_type === 'add_on' && b.status === 'active',
          );
          if (assignment) {
            await removeMemberBundle({
              userId: member.userId,
              assignmentId: assignment.assignment_id,
            }).unwrap();
          }
        }

        clinicians.memberBundleChanged({
          member_id: member.userId,
          tier_changed: tierChanged,
          from_tier_id: currentTierId || undefined,
          to_tier_id: selectedTierId || undefined,
          addons_added: addOnsToAdd.length,
          addons_removed: addOnsToRemove.length,
          addon_ids_added: addOnsToAdd,
          addon_ids_removed: addOnsToRemove,
          addon_names_added: addOnsToAdd.map(
            id => availableAddOns.find(a => a.bundle_id === id)?.name || id,
          ),
          addon_names_removed: addOnsToRemove.map(
            id => memberDetail.bundles?.find(b => b.bundle_id === id)?.bundle_name || id,
          ),
          final_addon_count: selectedAddOnIds.size,
        });
      }

      enqueueSnackbar('Member updated successfully', { variant: 'success' });
      onSuccess();
    } catch (error) {
      const message =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
          ? String(error.data.message)
          : 'Failed to update member';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [
    member,
    memberDetail,
    hasRolesChanges,
    hasMembershipChanges,
    hasPlanChanges,
    canSelectBundles,
    selectedRoles,
    selectedTierId,
    selectedAddOnIds,
    needsCosign,
    supervisorUserId,
    hasSupervisorChange,
    canPrepareOnBehalf,
    currentTierBundleId,
    currentTierAssignment,
    availableAddOns,
    tiers,
    addOns,
    updateRoles,
    updateMembershipInfo,
    changeMemberBundle,
    addMemberBundle,
    removeMemberBundle,
    enqueueSnackbar,
    onSuccess,
    clinicians,
  ]);

  const deactivate = useCallback(async () => {
    if (!member) return;
    try {
      await removeMember({ userId: member.userId }).unwrap();
      clinicians.memberDeactivated({ member_id: member.userId });
      enqueueSnackbar('Member deactivated successfully', { variant: 'success' });
      onSuccess();
    } catch (error) {
      const message =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
          ? String(error.data.message)
          : 'Failed to deactivate member';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [member, removeMember, enqueueSnackbar, onSuccess, clinicians]);

  const isSaving =
    isUpdatingRoles ||
    isUpdatingMembershipInfo ||
    isChangingBundle ||
    isAddingBundle ||
    isRemovingBundle;
  const isAutosaving = isUpdatingProfile || isUpdatingBillingInfo;
  const isLoading = isLoadingDetail || isLoadingMembershipInfo || isCatalogLoading;
  const isEditingSelf = member?.userId === currentUserId;

  return {
    // queries
    memberDetail,
    membershipInfo,
    billingEntities,
    entitiesLoading,
    serviceFacilities,
    facilitiesLoading,
    tiers,
    availableAddOns,
    isLoading,
    isSaving,
    isAutosaving,
    isDeactivating,
    isCatalogLoading,
    // catalog / context
    canSelectBundles,
    isClinician,
    isOperationsSupport,
    isTrialing,
    currentTierBundleId,
    // selection state
    selectedRoles,
    selectedTierId,
    selectedAddOnIds,
    needsCosign,
    supervisorUserId,
    canPrepareOnBehalf,
    profileData,
    billingData,
    // setters / handlers
    toggleRole,
    changeTier,
    toggleAddOn,
    setNeedsCosign,
    setSupervisorUserId,
    setCanPrepareOnBehalf,
    setProfileData,
    setBillingData,
    // change flags
    hasRolesChanges,
    hasMembershipChanges,
    hasPlanChanges,
    // cost
    currentCost,
    projectedCost,
    // commits
    commitChanges,
    deactivate,
    persistProfile,
    persistBilling,
    // misc
    isEditingSelf,
  };
}
